import type { ReactNode } from "react";
import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import { SaintVersusCopiesBlock } from "@/components/SaintVersusCopiesBlock";

const costRelated = [
  {
    title: "SAINT\u00ae Disclaimer: Look-Alike Programs Are Not SAINT\u00ae",
    href: "/learn/saint-disclaimer",
    description:
      "Our formal disclaimer: Cognitive FX and other \u201cfMRI-guided\u201d programs are not SAINT\u00ae",
  },
  {
    title: "SAINT\u00ae Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is SAINT® TMS?",
    href: "/learn/what-is-saint-tms",
    description: "FDA clearance, protocol, and how SAINT differs from TMS",
  },
  {
    title: "SAINT® vs fMRI-Guided Accelerated TMS",
    href: "/learn/saint-vs-accelerated-tms",
    description:
      "What's the same, what isn't, and what the FDA clearance covers",
  },
  {
    title: "SAINT® vs SPRAVATO®",
    href: "/learn/saint-vs-spravato",
    description: "Device vs medication for treatment-resistant depression",
  },
  {
    title: "SAINT® vs Ketamine",
    href: "/learn/saint-vs-ketamine",
    description: "Magnetic stimulation vs IV ketamine compared",
  },
  {
    title: "SAINT® vs ECT",
    href: "/learn/saint-vs-ect",
    description:
      "Non-invasive magnetic stimulation vs electroconvulsive therapy",
  },
  {
    title: "Financing Options",
    href: "/contents/patient-info/financing-options",
    description: "Payment plan information for SAINT® treatment",
  },
  {
    title: "Does Insurance Cover SAINT®?",
    href: "/learn/does-insurance-cover-saint-tms",
    description: "Coverage, prior authorization, and benefits verification",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Book a free consultation with our clinical team",
  },
];

const costFaqs: LearnFAQ[] = [
  {
    question: "How much does SAINT® TMS cost?",
    answer:
      "SAINT® at Neuro Wellness TMS Centers of America starts at $25,000 for a complete five-day course, including the fMRI targeting scan, all 50 sessions, the clinical team, and follow-up. Coverage varies by plan; we verify benefits for free, and financing is available case by case.",
  },
  {
    question: "Is there a cheaper alternative to SAINT®?",
    answer:
      'Some clinics sell "fMRI-guided accelerated TMS" for about $9,900. These programs copy SAINT\'s five-day schedule but do not use the FDA-cleared SAINT targeting system, are described by their own providers as off-label, and have not been tested in the SAINT clinical trials. They are a different treatment, not a cheaper version of SAINT®.',
  },
  {
    question: "Why do some websites say SAINT costs $30,000–36,000?",
    answer:
      "That range reflects pricing at some clinics. At Neuro Wellness, SAINT® starts at $25,000. Medicare has also set a hospital outpatient payment of $19,703 for SAINT®.",
  },
  {
    question: "Does insurance cover SAINT TMS?",
    answer:
      "SAINT® may be covered; coverage varies by plan. SAINT uses Category III CPT codes, and some insurers do not yet cover it, so it is often self-pay. Neuro Wellness verifies your benefits for free, whatever carrier you have. We never guarantee coverage — we confirm what your plan allows before you commit.",
  },
  {
    question: "Does Medicare cover SAINT?",
    answer:
      "Medicare fee-for-service reimbursement is available for SAINT®'s Category III CPT codes. CMS established a hospital outpatient payment of $19,703 effective July 1, 2025. However, medical-necessity criteria must be met, and Medicare Advantage plans may add their own prior-authorization steps. Medicare coverage does not guarantee zero cost-sharing.",
  },
  {
    question: "Why does SAINT cost more than regular TMS?",
    answer:
      "SAINT® uses the Magnus Neuromodulation System, requires an fMRI scan for personalized targeting, delivers ten sessions per day for five consecutive days, and needs a full clinical team on-site throughout the treatment week. The cost reflects the device, imaging, staffing, and the week-long intensive format. Standard TMS uses a conventional device, no fMRI, and one session per day over six weeks.",
  },
  {
    question: "Is there financing for SAINT?",
    answer:
      "Financing is available on a case-by-case basis and is discussed during your consultation. Some patients also use HSA or FSA funds, since SAINT® is an FDA-cleared medical treatment and is generally an eligible expense. Contact the clinic for current financing information.",
  },
  {
    question: "Are there cheaper SAINT alternatives, and what's the trade-off?",
    answer:
      "Lower-cost accelerated protocols are commonly marketed at roughly a third of SAINT's price — for example, around $9,900 versus $30,000 or more at some clinics. Neuro Wellness's SAINT® price starts at $25,000, below the $30,000+ figure often quoted elsewhere. Those lower-priced protocols typically use a conventional device, less imaging, a different targeting method, or a different staffing model. They do not use the FDA-cleared SAINT targeting system, and the SAINT trial outcomes do not transfer to them.",
  },
  {
    question: "Is the consultation free?",
    answer:
      "Yes. Your first consultation at Neuro Wellness is free. We'll tell you the price, what's included, whether your insurance might apply, and how soon you could start.",
  },
  {
    question: "Are travel and lodging included in the cost?",
    answer:
      "No. Out-of-town patients arrange their own lodging. We can help you plan the timing so you only make one trip. Learn more about traveling for SAINT® on our travel page.",
  },
  {
    question: "What if SAINT doesn't work for me?",
    answer:
      "Not everyone reaches remission. If SAINT® does not produce enough improvement, other options remain — standard TMS, SPRAVATO® (esketamine), medication adjustments, or psychotherapy. Neuro Wellness offers all of these. Individual results may vary.",
  },
];

export const saintCost = makeArticle({
  slug: "saint-tms-cost",
  title: "SAINT® TMS Cost 2026: From $25,000 | Why $9,900 Isn't SAINT",
  description:
    "SAINT® at Neuro Wellness starts at $25,000 for the full 5-day course. See what's included, insurance and Medicare options, and why lower-cost \"accelerated TMS\" isn't SAINT®.",
  canonical: "/learn/saint-tms-cost",
  h1: "How Much Does SAINT® TMS Cost in 2026?",
  breadcrumbLabel: "SAINT® TMS Cost",
  shortAnswer:
    "SAINT® at Neuro Wellness TMS Centers of America starts at $25,000 for a complete five-day course of treatment. That includes the fMRI targeting scan, all 50 sessions, the clinical team, and follow-up. SAINT® may be covered; coverage varies by plan. We verify your benefits for free, whatever carrier you have, and financing is available on a case-by-case basis.",
  keyTakeaways: [
    "SAINT® at Neuro Wellness starts at $25,000 for a complete five-day course.",
    "The price includes the fMRI targeting scan, all 50 sessions, the clinical team, and follow-up.",
    "SAINT® may be covered; coverage varies by plan — we verify your benefits for free.",
    "Medicare fee-for-service reimbursement is available; CMS set a hospital outpatient payment of $19,703 effective July 2025.",
    "Lower-cost accelerated protocols do not use the FDA-cleared SAINT targeting system, and the SAINT trial outcomes do not transfer to them.",
  ],
  sections: [
    {
      heading: "Quick Answer",
      paragraphs: [
        "SAINT® at Neuro Wellness TMS Centers of America starts at $25,000 for a complete course of treatment. That price covers the fMRI targeting scan, all 50 sessions over five consecutive days, the full clinical team on-site throughout the week, and follow-up. SAINT® may be covered; coverage varies by plan. We verify your benefits for free, whatever carrier you have, and financing is available on a case-by-case basis. You can [[check your insurance options here|/check-insurance-options]] or [[see if you may be a candidate|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "What You're Paying For",
      paragraphs: [
        "SAINT® is not a single procedure. It is a complete, intensive course of treatment. The price reflects the technology, the imaging, the staffing, and the week-long format. Here is what a full course includes:",
      ],
      list: [
        "A psychiatric consultation and eligibility evaluation to confirm SAINT® is appropriate for your situation",
        "A structural and functional MRI (fMRI) scan to map your brain's depression-related networks",
        "Personalized targeting using Magnus Medical's proprietary SAINT software to find your individual stimulation site — the specific element the FDA cleared under K220177",
        "50 treatment sessions delivered over five consecutive days: 10 sessions per day, each with 10 minutes of stimulation and a 50-minute break",
        "Neuronavigation so the coil is positioned on your exact target every session",
        "Motor threshold testing to set your stimulation intensity",
        "A full clinical team on-site throughout the treatment week, including the psychiatrist and TMS technicians trained on the Magnus system",
        "Monitoring and follow-up during and after your treatment week",
      ],
      paragraphsAfter: [
        "Standard TMS does not use an fMRI scan, does not use personalized targeting software, and delivers one session per day over six to nine weeks. The fMRI and the accelerated, intensive format are what make SAINT® distinct — and they are significant cost drivers compared to conventional TMS. Learn more about [[what SAINT® is|/learn/what-is-saint-tms]] and about [[what treatment-resistant depression means|/learn/what-is-treatment-resistant-depression]].",
      ],
    },
    {
      heading: "Insurance: Commercial Plans, Medicare, and FEHB",
      paragraphs: [
        "Insurance coverage for SAINT® is currently limited and varies by plan. SAINT® is billed under Category III CPT codes (0889T–0892T), which are newer tracking codes that many plans have not yet established coverage policies for. Prior authorization is common. Being in-network with a plan does not mean SAINT® is covered for a given person — coverage varies by plan.",
      ],
    },
    {
      heading: "Commercial Insurance",
      paragraphs: [
        "Some commercial plans cover SAINT® on a case-by-case basis with prior authorization. The manufacturer reports growing commercial coverage, but many patients still self-pay because their plan has not yet established a coverage policy for the Category III codes. Neuro Wellness verifies your benefits before treatment and provides a written estimate. The only way to know your situation is verification. [[Check your insurance options here|/check-insurance-options]].",
      ],
    },
    {
      heading: "Medicare",
      paragraphs: [
        "Medicare fee-for-service reimbursement is available for SAINT®'s Category III CPT codes. The Centers for Medicare & Medicaid Services (CMS) established a hospital outpatient payment of $19,703 for SAINT® treatment, effective July 1, 2025. However, Medicare coverage still requires medical-necessity criteria to be met, and Medicare Advantage plans may add their own prior-authorization steps. Medicare coverage does not guarantee that your cost-sharing will be zero — deductibles, coinsurance, and copayments may still apply. Coverage varies by plan.",
      ],
    },
    {
      heading: "FEHB and BCBS Federal",
      paragraphs: [
        "The Federal Employees Health Benefits (FEHB) program and Blue Cross Blue Shield Federal Employee Program may cover SAINT® in select states. Coverage is not guaranteed and varies by plan and by the specific FEHB carrier. If you are a federal employee, we verify your benefits through your specific plan. [[Learn more about FEHB and SAINT®|/federal-employee-saint-tms]] or [[check your insurance options|/check-insurance-options]].",
      ],
    },
    {
      heading: "Self-Pay and Financing",
      paragraphs: [
        "Self-pay is the most common and fastest option at Neuro Wellness. SAINT® starts at $25,000 for a complete five-day course. Most self-pay patients move from consultation to treatment within a few days, without waiting for insurance authorization.",
        "Financing is available on a case-by-case basis and is discussed during your consultation. Some patients use healthcare financing services to spread the cost over time. [[See financing options|/contents/patient-info/financing-options]].",
        "HSA and FSA funds: SAINT® is an FDA-cleared medical treatment and is generally an eligible expense for health savings accounts (HSA) and health flexible spending accounts (FSA), including deductibles, coinsurance, and self-pay balances. Eligibility rules come from the IRS and your account administrator, so confirm with them and keep itemized receipts. This is general information, not tax advice.",
      ],
    },
    {
      heading: "Cost vs Alternatives: SAINT vs Other Treatments",
      paragraphs: [
        "SAINT® is one of several treatments for treatment-resistant depression. The right choice depends on your clinical situation, your budget, and how quickly you need results. Here is how the costs and timelines compare. Individual results may vary for every treatment listed below.",
      ],
      table: {
        headers: [
          "Treatment",
          "Typical cost range",
          "Insurance pathway",
          "Clinic days",
          "Time to typical response",
          "Source",
        ],
        rows: [
          [
            "SAINT® (5-day fMRI-guided)",
            "Starts at $25,000 (self-pay at NWCOA)",
            "May be covered; varies by plan; Cat III codes",
            "5 consecutive days",
            "Days (in trials)",
            "NWCOA; Cole et al. 2022",
          ],
          [
            "fMRI-guided accelerated TMS (non-SAINT)",
            "Often ~$9,900 (varies)",
            "Typically not covered",
            "5–10 days",
            "Not established for this protocol",
            "Market reports; not SAINT evidence",
          ],
          [
            "Standard TMS",
            "Lower; often insurance-covered",
            "Often covered (with criteria)",
            "~30–36 over 6 weeks",
            "2–4 weeks",
            "O'Reardon et al. 2007",
          ],
          [
            "SPRAVATO® (esketamine)",
            "Varies; often covered for TRD",
            "Often covered for TRD (with criteria)",
            "Twice weekly, then taper",
            "Weeks",
            "FDA label; Janssen",
          ],
          [
            "IV Ketamine",
            "Varies; typically not covered",
            "Typically not covered",
            "6 sessions over 2–3 weeks",
            "Days to weeks",
            "Off-label; limited published data",
          ],
          [
            "ECT",
            "Varies; often covered",
            "Often covered (with criteria)",
            "2–3/week for 2–4 weeks",
            "Weeks",
            "Established; multiple RCTs",
          ],
        ],
      },
      paragraphsAfter: [
        "The $9,900 vs $30,000+ comparison that appears in some marketing refers to lower-cost accelerated protocols versus the full SAINT price at some clinics. Neuro Wellness's SAINT® price starts at $25,000, below the $30,000+ figure often quoted elsewhere. The lower-priced protocols do not use the FDA-cleared SAINT targeting system, and the SAINT trial outcomes do not transfer to them. A lower price is not inherently a problem, but it usually means you are getting a different protocol, not the same protocol for less. Read more about [[SAINT® vs accelerated TMS|/learn/saint-vs-accelerated-tms]].",
      ],
    },
    {
      heading: "The Cost of Waiting: Time Off Work and Travel",
      paragraphs: [
        "Standard TMS involves 30 to 36 sessions over six to nine weeks. That means daily appointments for weeks — time off work, childcare arrangements, and weeks of commuting before you know whether the treatment is working. SAINT® compresses 50 sessions into five consecutive days. You are at the clinic for full days during the treatment week, but you are done in a week.",
        "For many people, the bigger costs of depression are the ones that do not show up on a bill: months of lost productivity, missed work, strained relationships, and ongoing medication trials. A treatment that can work in days changes that math.",
        "For out-of-state patients, SAINT® also means one trip to Coral Springs instead of weekly visits for six weeks. We help with logistics — [[learn about traveling for SAINT®|/learn/travel-for-saint-tms]] and about [[SAINT® locations|/learn/saint-tms-locations]].",
      ],
    },
    {
      heading: "Free Benefits Verification",
      paragraphs: [
        "The best way to get an exact number is a short phone call. We verify your benefits, tell you the price, what's included, whether your insurance might apply, and how soon you could start. There is no charge for the benefits check or the consultation.",
      ],
      links: [
        {
          to: "/check-insurance-options",
          text: "Check my insurance benefits",
        },
        {
          to: "/am-i-a-candidate-screener",
          text: "See if I'm a candidate",
        },
        { to: "/saint", text: "Learn more about SAINT®" },
      ],
    },
    {
      heading: "Comparison Table",
      paragraphs: [],
      table: {
        headers: [
          "Dimension",
          "SAINT®",
          "fMRI-guided accelerated TMS (non-SAINT)",
          "Standard TMS",
        ],
        rows: [
          [
            "FDA-cleared personalized targeting system",
            "Yes (K220177)",
            "No — off-label use of a cleared device",
            "No",
          ],
          [
            "Uses Magnus SAINT Neuromodulation System",
            "Yes",
            "No — conventional TMS device",
            "No",
          ],
          [
            "Clinic verified by Magnus",
            "Yes (Verified SAINT® Provider)",
            "No",
            "No",
          ],
          [
            "Pulse pattern (iTBS)",
            "Yes",
            "May use iTBS or other patterns",
            "10 Hz or iTBS",
          ],
          ["Sessions per day", "10", "Varies (often 5–10)", "1"],
          [
            "Days",
            "5 consecutive days",
            "Varies (often 5–10 days)",
            "~30–36 over 6 weeks",
          ],
          [
            "Published randomized trial for this exact system",
            "Yes (Cole et al. 2022)",
            "No — ask for the clinic's own data",
            "Yes (multiple pivotal RCTs)",
          ],
          [
            "Who chooses the target",
            "fMRI-based SAINT targeting software",
            "fMRI or scalp measurement (varies)",
            "Scalp measurement (5.5 cm rule)",
          ],
          [
            "Typical price",
            "Starts at $25,000 (self-pay)",
            "Often ~$9,900 (varies)",
            "Lower; often insurance-covered",
          ],
          [
            "Insurance pathway",
            "May be covered; varies by plan; Cat III codes",
            "Typically not covered",
            "Often covered (with criteria)",
          ],
        ],
      },
    },
  ],
  faqs: costFaqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(9):816–821.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32631493/",
    },
    {
      citation:
        "U.S. FDA. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology (September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Centers for Medicare & Medicaid Services. Hospital Outpatient Prospective Payment System — SAINT® Category III CPT codes 0889T–0892T. Effective July 1, 2025.",
      url: "https://www.cms.gov/",
    },
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of transcranial magnetic stimulation in the acute treatment of major depression. Biol Psychiatry. 2007;62(11):1208–1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17573044/",
    },
  ],
  about: {
    "@type": "MedicalTherapy",
    name: "SAINT Neuromodulation System",
  },
  relatedLinks: costRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick Answer", href: "#quick-answer" },
    { label: "What You're Paying For", href: "#what-youre-paying-for" },
    {
      label: "Insurance & Medicare",
      href: "#insurance-commercial-plans-medicare-and-fehb",
    },
    { label: "Self-Pay & Financing", href: "#self-pay-and-financing" },
    {
      label: "Cost vs Alternatives",
      href: "#cost-vs-alternatives-saint-vs-other-treatments",
    },
    {
      label: "Cost of Waiting",
      href: "#the-cost-of-waiting-time-off-work-and-travel",
    },
    {
      label: "Free Benefits Verification",
      href: "#free-benefits-verification",
    },
    { label: "Comparison Table", href: "#comparison-table" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});

/**
 * Insert the "SAINT vs lower-cost copies" block right after the Quick Answer
 * section, and move the existing Comparison Table section up so it sits
 * immediately after the block (as requested). All copy is real HTML text so
 * search engines and AI answer engines can quote it.
 */
const _orderedSections = (() => {
  const blockSection = {
    heading: "",
    body: <SaintVersusCopiesBlock />,
  };
  const rest = saintCost.sections;
  const quickIdx = rest.findIndex((s) => s.heading === "Quick Answer");
  const compIdx = rest.findIndex((s) => s.heading === "Comparison Table");
  if (quickIdx === -1) return rest;
  // Pull the Comparison Table section out of its current position.
  const comparison = compIdx !== -1 ? rest[compIdx] : null;
  const withoutComparison = rest.filter((_, i) => i !== compIdx);
  // Re-find quick index after filtering (indices shifted).
  const newQuickIdx = withoutComparison.findIndex(
    (s) => s.heading === "Quick Answer",
  );
  const before = withoutComparison.slice(0, newQuickIdx + 1);
  const after = withoutComparison.slice(newQuickIdx + 1);
  return [
    ...before,
    blockSection,
    ...(comparison ? [comparison] : []),
    ...after,
  ];
})();

saintCost.sections = _orderedSections as typeof saintCost.sections;
